import { motion } from 'framer-motion';
import { Scale } from 'lucide-react';
import type { DossierSectionProps } from './types';
import type { CourtCase } from '@/types/profiler';

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function isCriminal(c: CourtCase): boolean {
  const t = (c.case_type ?? '').toUpperCase();
  return t.includes('CRIMINAL') || t.includes('FIR') || t.includes('CBI') || t.includes('POCSO');
}

// ── case type badge ───────────────────────────────────────────────────────────

function CaseTypeBadge({ caseType }: { caseType?: string }) {
  const criminal = isCriminal({ case_type: caseType });
  const color = criminal ? '#FF1F1F' : '#2979FF';
  return (
    <span
      style={{
        background: `${color}15`,
        color,
        border: `1px solid ${color}30`,
        fontSize: 8,
        fontFamily: 'monospace',
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
      }}
    >
      {caseType ?? 'CIVIL'}
    </span>
  );
}

// ── status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status?: string }) {
  const s = (status ?? '').toLowerCase();
  const color = s.includes('pending') || s.includes('active')
    ? '#F0B429'
    : s.includes('disposed') || s.includes('closed')
    ? '#00C853'
    : '#5A7A9A';
  return (
    <span
      style={{
        color,
        fontSize: 9,
        fontFamily: 'monospace',
        fontWeight: 700,
      }}
    >
      {status ?? '—'}
    </span>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function Legal({ pipeline }: DossierSectionProps) {
  const cases: CourtCase[] = pipeline?.court?.cases ?? [];

  const criminalCases = cases.filter(isCriminal);
  const civilCases = cases.filter(c => !isCriminal(c));

  if (cases.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div
          style={{
            background: '#0C1118',
            border: '1px solid #182030',
            borderRadius: 8,
            padding: '48px 24px',
            textAlign: 'center',
          }}
        >
          <Scale size={28} style={{ color: '#283A50', margin: '0 auto 10px' }} />
          <p style={{ color: '#283A50', fontSize: 11, fontFamily: 'monospace' }}>
            No court records found in eCourts NJDG database
          </p>
          <p style={{ color: '#182030', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>
            Screened against Supreme Court, High Courts, and District Courts
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* Count badges */}
      <div className="flex items-center gap-3">
        {criminalCases.length > 0 && (
          <span
            style={{
              background: '#FF1F1F18',
              color: '#FF1F1F',
              border: '1px solid #FF1F1F30',
              fontSize: 10,
              fontFamily: 'monospace',
              fontWeight: 700,
              padding: '4px 12px',
              borderRadius: 99,
            }}
          >
            {criminalCases.length} CRIMINAL CASE{criminalCases.length > 1 ? 'S' : ''}
          </span>
        )}
        {civilCases.length > 0 && (
          <span
            style={{
              background: '#2979FF18',
              color: '#2979FF',
              border: '1px solid #2979FF30',
              fontSize: 10,
              fontFamily: 'monospace',
              fontWeight: 700,
              padding: '4px 12px',
              borderRadius: 99,
            }}
          >
            {civilCases.length} CIVIL CASE{civilCases.length > 1 ? 'S' : ''}
          </span>
        )}
        <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
          {cases.length} total — eCourts NJDG
        </span>
      </div>

      {/* Cases table */}
      <div
        style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, overflow: 'hidden' }}
      >
        {/* Table header */}
        <div
          className="grid gap-2 px-4 py-2"
          style={{
            gridTemplateColumns: '110px 130px 100px 1fr 80px 80px 80px',
            background: '#090D13',
            borderBottom: '1px solid #182030',
          }}
        >
          {['CNR', 'COURT', 'TYPE', 'PARTIES', 'FILED', 'STATUS', 'NEXT HEARING'].map(h => (
            <span key={h} style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.08em' }}>
              {h}
            </span>
          ))}
        </div>

        {cases.map((c, i) => {
          const criminal = isCriminal(c);
          const accentColor = criminal ? '#FF1F1F' : '#2979FF';
          return (
            <div
              key={i}
              className="grid gap-2 px-4 py-3 items-start"
              style={{
                gridTemplateColumns: '110px 130px 100px 1fr 80px 80px 80px',
                borderBottom: i < cases.length - 1 ? '1px solid #0F1928' : undefined,
                borderLeft: `3px solid ${accentColor}`,
              }}
            >
              <span style={{ color: '#DCE8F5', fontSize: 9, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {c.cnr ?? '—'}
              </span>
              <span style={{ color: '#5A7A9A', fontSize: 9, wordBreak: 'break-word', lineHeight: 1.4 }}>
                {c.court ?? '—'}
              </span>
              <CaseTypeBadge caseType={c.case_type} />
              <span style={{ color: '#DCE8F5', fontSize: 9, lineHeight: 1.4, wordBreak: 'break-word' }}>
                {c.parties ?? '—'}
              </span>
              <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                {formatDate(c.filed_date)}
              </span>
              <StatusBadge status={c.status} />
              <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                {formatDate(c.next_hearing)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div
        style={{ background: '#060E1A', border: '1px solid #1E2B3F', borderRadius: 8, padding: '10px 14px' }}
      >
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', lineHeight: 1.6 }}>
          Court records sourced from eCourts NJDG (National Judicial Data Grid) via automated lookup.
          Data reflects publicly available case information. Cases may be incomplete or subject to
          confidentiality orders. Criminal cases require MLRO review before adverse determination.
        </p>
      </div>
    </motion.div>
  );
}
